/**
 * Company: SPHMedia
 * Description: [kaki makan search and filter hook]
 */

import fetchBHApi from "@helper/fetchBHApi";
import { useCallback, useEffect, useState } from "react";

import { ProcessedArticleData } from "./transformer/useOSResponse";

type useKakiMakanSearchAndFilterData = {
  handleQueryChange: (options: {
    search?: string;
    filter?: string | null;
  }) => Promise<void>;
  searchQuery: string;
  filterQuery: string | null;
};

type useKakiMakanSearchAndFilterProps = {
  onDataFetched: (data: ProcessedArticleData[]) => void;
  onLoading: (isLoading: boolean) => void;
  onReset: () => void;
  onQueryChange?: (search: string, filter: string | null) => void;
};

/**
 * Custom React Hook: useKakiMakanSearchAndFilterHook
 * Description: [Hook to manage search and filter functionalities for Kaki Makan]
 *
 * Key Responsibilities:
 * 1. Stores current searchQuery and filterQuery in state.
 * 2. Fetches new restaurant list whenever search or filter changes.
 * 3. Calls parent callbacks (onDataFetched, onLoading, onReset, onQueryChange).
 *
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useKakiMakanSearchAndFilter({
  onDataFetched,
  onLoading,
  onReset,
  onQueryChange,
}: useKakiMakanSearchAndFilterProps): useKakiMakanSearchAndFilterData {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filterQuery, setFilterQuery] = useState<string | null>(null);

  const fetchKakiMakanRestaurants = useCallback(
    async (search?: string, filter?: string | null) => {
      try {
        onLoading(true);

        // Build query parameters
        const params = new URLSearchParams();
        if (search) params.append("search", search);
        if (filter) params.append("filter", filter);

        const response = await fetchBHApi<ProcessedArticleData[]>(
          `kaki-makan/restaurants?${params.toString()}`,
          "GET",
        );

        onDataFetched(response);

        return response;
      } catch (err) {
        console.error("Error fetching Kaki Makan data:", err);
        onDataFetched([]);
      } finally {
        onLoading(false);
      }
    },
    [onDataFetched, onLoading],
  );

  // Unified handler
  const handleQueryChange = useCallback(
    async (options: { search?: string; filter?: string | null }) => {
      const newSearch =
        options.search !== undefined ? options.search : searchQuery;
      const newFilter =
        options.filter !== undefined ? options.filter : filterQuery;

      // Update state
      setSearchQuery(newSearch);
      setFilterQuery(newFilter);

      if (!newSearch && !newFilter) {
        // No search/filter → reset to default
        onReset();
        return;
      } else {
        // Call API with latest values
        await fetchKakiMakanRestaurants(newSearch, newFilter);
      }
    },
    [fetchKakiMakanRestaurants, onReset, searchQuery, filterQuery],
  );

  useEffect(() => {
    if (onQueryChange) {
      onQueryChange(searchQuery, filterQuery);
    }
  }, [searchQuery, filterQuery, onQueryChange]);

  return { handleQueryChange, searchQuery, filterQuery };
}
